/**
 * IndexChannelSelector - Dropdown to select and parse index channel
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import { Search as ParseIcon } from '@mui/icons-material';
import { Chat } from '@/types/api';
import { useChatLabel } from '@/hooks/useChatLabel';

interface IndexChannelSelectorProps {
  chats: Chat[];
  selectedIndexChannelId: number | null;
  onSelect: (chatId: number | null) => void;
  onParse: () => void;
  isParsing: boolean;
  isDisabled?: boolean;
  entriesCount?: number;
  // Optional separate message range for index channel
  indexStartMessageId?: string;
  indexEndMessageId?: string;
  onIndexStartChange?: (value: string) => void;
  onIndexEndChange?: (value: string) => void;
}

const IndexChannelSelector: React.FC<IndexChannelSelectorProps> = ({
  chats,
  selectedIndexChannelId,
  onSelect,
  onParse,
  isParsing,
  isDisabled = false,
  entriesCount,
  indexStartMessageId,
  indexEndMessageId,
  onIndexStartChange,
  onIndexEndChange,
}) => {
  const chatLabel = useChatLabel();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl fullWidth disabled={isDisabled}>
          <InputLabel>Index Channel (Optional)</InputLabel>
          <Select
            value={selectedIndexChannelId ?? ''}
            onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : null)}
            label="Index Channel (Optional)"
            displayEmpty
          >
            <MenuItem value="">
              <em>None - Skip index channel</em>
            </MenuItem>
            {chats.map((chat) => (
              <MenuItem key={chat.id} value={chat.id}>
                {chatLabel(chat)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={onParse}
          disabled={!selectedIndexChannelId || isParsing || isDisabled}
          startIcon={isParsing ? <CircularProgress size={20} /> : <ParseIcon />}
          sx={{ minWidth: 140, height: 56 }}
        >
          {isParsing ? 'Parsing...' : 'Parse Index'}
        </Button>

        {entriesCount !== undefined && entriesCount > 0 && (
          <Chip
            label={`${entriesCount} ${entriesCount === 1 ? 'entry' : 'entries'} found`}
            color="success"
            variant="outlined"
          />
        )}
      </Box>

      {/* Optional: Separate message range for index channel */}
      {selectedIndexChannelId && onIndexStartChange && onIndexEndChange && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', pl: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
            Index channel range (optional):
          </Typography>
          <TextField
            label="Index Start (optional)"
            type="number"
            size="small"
            value={indexStartMessageId || ''}
            onChange={(e) => onIndexStartChange(e.target.value)}
            placeholder="Uses main chat start"
            disabled={isDisabled}
            sx={{ width: 200 }}
          />
          <TextField
            label="Index End (optional)"
            type="number"
            size="small"
            value={indexEndMessageId || ''}
            onChange={(e) => onIndexEndChange(e.target.value)}
            placeholder="Uses main chat end"
            disabled={isDisabled}
            sx={{ width: 200 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default IndexChannelSelector;
